'use client'

import { useState } from 'react'

import IconUser from '@/components/assets/icon_user.svg'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import VerifiedBadge from '@/components/VerifiedBadge'
import ViverseSkeleton from '@/components/ViverseSkeleton'
import { ALLOWED_ASSETS_DOMAINS } from '@/constants/assets'
import { Link } from '@/i18n/navigation'
import { akamaiImageLoader } from '@/lib/image-loader'
import { cn } from '@/lib/utils'

type CreatorAvatarProps = {
  communityUserId: string
  headIconUrl: string
  displayName: string
  disableDrag?: boolean
  isVerified?: boolean
  size?: 'sm' | 'default'
}

export default function CreatorAvatar({
  communityUserId,
  headIconUrl,
  displayName,
  disableDrag,
  isVerified,
  size = 'default',
}: CreatorAvatarProps) {
  const [fallbackSource, setFallbackSource] = useState<string | null>(null)
  // sm: size-16 (64px) × 2 DPR = 128px; default: size-24 (96px) × 2 DPR = 192px
  const imageWidth = size === 'sm' ? 128 : 192

  const useAkamaiLoader =
    headIconUrl && ALLOWED_ASSETS_DOMAINS.some((domain) => headIconUrl.includes(domain))

  const hasFallenBack = fallbackSource === headIconUrl

  const optimizedHeadIconUrl =
    useAkamaiLoader && !hasFallenBack
      ? akamaiImageLoader({ src: headIconUrl, width: imageWidth })
      : headIconUrl

  return (
    <Link
      target="_blank"
      draggable={disableDrag ? false : undefined}
      className={cn(
        'group flex cursor-pointer flex-col gap-4 py-2 pb-5',
        disableDrag && 'select-none',
      )}
      href={`/profile/${communityUserId}`}
    >
      <Avatar
        className={cn('bg-vive-brand-60 mx-auto', size === 'sm' ? 'size-16' : 'size-18 md:size-24')}
      >
        <AvatarImage
          src={optimizedHeadIconUrl}
          alt={displayName}
          className="object-cover transition-all duration-300 group-hover:scale-105 group-hover:brightness-105"
          onError={() => {
            if (useAkamaiLoader && !hasFallenBack) {
              setFallbackSource(headIconUrl)
            }
          }}
        />
        <AvatarFallback>
          {!headIconUrl ? (
            <IconUser className="bg-vive-brand-60 size-full" />
          ) : (
            <Skeleton className="bg-vive-brand-60 size-full" />
          )}
        </AvatarFallback>
      </Avatar>

      <div
        className={cn('flex w-[100px] flex-col', size === 'sm' ? 'md:w-[152px]' : 'md:w-[168px]')}
      >
        <h2 className="subheader-3 flex items-center justify-center gap-1 truncate text-center">
          <span className="truncate">{displayName}</span>
          <VerifiedBadge isVerified={isVerified} />
        </h2>
      </div>
    </Link>
  )
}

export function CreatorAvatarSkeleton() {
  return (
    <div className="group flex cursor-pointer flex-col gap-4 py-2 pb-5">
      <ViverseSkeleton className="mx-auto size-18 rounded-full md:size-24" />
      <div className="flex flex-col md:min-w-[168px]">
        <ViverseSkeleton className="mx-auto h-6 w-18" />
      </div>
    </div>
  )
}
